import type { Ref } from 'vue'
import { ref } from 'vue'

import type { AggregatedDasSuggestion } from '@/stay-list/stay-quick-view/dasSuggestionAggregator'

const MAX_WILDCARD_STACK_BARS = 10

type WildcardMember = Omit<AggregatedDasSuggestion, 'otherWildcardChildren'>

// Plie/déplie les enfants d'une suggestion wildcard (BUILD-830) : repliée, seuls les diagnostics
// sélectionnés restent visibles, ou à défaut le diagnostic par défaut du wildcard ; dépliée, tous
// s'affichent.
export function useWildcardFolding(selectedDasReferences: Ref<Set<string>>) {
  // Keyed by the wildcard's aggregate reference (its representative's reference — stable even once
  // a different member becomes the displayed default, see defaultOverrides below).
  const expandedWildcards = ref<Set<string>>(new Set())

  // Which member is shown as the wildcard's default diagnostic once folded back with nothing
  // selected. Unset by default (falls back to the representative), and set to the last member
  // deselected when a wildcard's selection drops back to zero (UX request).
  const defaultOverrides = ref<Map<string, string>>(new Map())

  function toggleWildcardExpansion(reference: string) {
    const next = new Set(expandedWildcards.value)
    if (next.has(reference)) {
      next.delete(reference)
    } else {
      next.add(reference)
    }
    expandedWildcards.value = next
  }

  function setWildcardDefault(groupReference: string, memberReference: string) {
    const next = new Map(defaultOverrides.value)
    next.set(groupReference, memberReference)
    defaultOverrides.value = next
  }

  function resetWildcardExpansion() {
    expandedWildcards.value = new Set()
    defaultOverrides.value = new Map()
  }

  function wildcardGroupMembers(suggestion: AggregatedDasSuggestion): WildcardMember[] {
    return [suggestion, ...suggestion.otherWildcardChildren]
  }

  // Folded: shows only the selected members, or — with nothing selected — the wildcard's default
  // diagnostic (its representative, unless overridden by defaultOverrides). Unfolded, or for a
  // non-wildcard suggestion: every member.
  function visibleWildcardMembers(suggestion: AggregatedDasSuggestion): WildcardMember[] {
    const members = wildcardGroupMembers(suggestion)
    if (suggestion.otherWildcardChildren.length === 0 || expandedWildcards.value.has(suggestion.reference)) {
      return members
    }

    const selectedMembers = members.filter((member) => selectedDasReferences.value.has(member.reference))
    if (selectedMembers.length > 0) {
      return selectedMembers
    }

    const defaultReference = defaultOverrides.value.get(suggestion.reference)
    return [members.find((member) => member.reference === defaultReference) ?? suggestion]
  }

  // One bar per folded (not currently shown) member, capped so a large wildcard doesn't grow an
  // unreasonably tall stack.
  function foldedWildcardStackBars(suggestion: AggregatedDasSuggestion) {
    const total = 1 + suggestion.otherWildcardChildren.length
    const visibleCount = visibleWildcardMembers(suggestion).length
    return Math.min(total - visibleCount, MAX_WILDCARD_STACK_BARS)
  }

  return {
    expandedWildcards,
    toggleWildcardExpansion,
    resetWildcardExpansion,
    setWildcardDefault,
    visibleWildcardMembers,
    foldedWildcardStackBars,
  }
}
